// ── Reseller credit packs: display copy of the server catalogue ──────────────
//
// Mirrors cadomation-backend config/creditPacks.js. The server re-prices every
// request from its own copy (the client only sends a pack id and quantity), so
// a drift here can misprice the SCREEN but never the invoice —
// creditPacks.test.js pins the figures so a drift fails the build instead.
//
// Only `days`, `ratePerCredit` and `listPriceUsd` are stated. Every price,
// margin and per-license figure on the Buy Credits page is derived below, so a
// rate change cannot leave a stale "keep $126" behind in a component.
//
// listPriceUsd is the same span at Cadomation's public prices (1 year $199,
// 3 years $499, longer spans as N × $199): the reseller's resale value.

export const CREDIT_PACKS = [
  { id: "1y", label: "1 Year", days: 365, ratePerCredit: 0.355, listPriceUsd: 199, badge: null },
  { id: "3y", label: "3 Years", days: 1095, ratePerCredit: 0.325, listPriceUsd: 597, badge: null },
  { id: "6y", label: "6 Years", days: 2190, ratePerCredit: 0.300, listPriceUsd: 1194, badge: "most_chosen" },
  { id: "15y", label: "15 Years", days: 5475, ratePerCredit: 0.250, listPriceUsd: 2985, badge: null },
  { id: "30y", label: "30 Years", days: 10950, ratePerCredit: 0.200, listPriceUsd: 5970, badge: "max_margin" },
];

export const BADGE_LABELS = {
  most_chosen: "Most popular",
  max_margin: "Highest margin",
};

// "Buy on the go" — pay within PAY_LATER_DAYS. Our team still approves the
// request before the credits land; this only moves the payment date.
export const PAY_LATER_PACK_IDS = ["1y"];
export const PAY_LATER_DAYS = 30;
export const MAX_PACK_QUANTITY = 20;

// The yardstick for every per-license figure: a one-year license, which is what
// a reseller most often issues and what $199 list price refers to.
export const DAYS_PER_YEAR = 365;
export const ONE_YEAR_LIST_PRICE = 199;

// ── Derived values — never stored ────────────────────────────────────────────

const round2 = (n) => Math.round(n * 100) / 100;

// days × rate in whole thousandths of a dollar, then to cents. Plain float
// maths gets the half-cent cases wrong — 365 × 0.355 is 129.57499… in binary,
// which would round to $129.57 against the $129.58 the price sheet (and the
// server, which does the same) charges.
const priceForDays = (days, ratePerCredit) =>
  Math.round((days * Math.round(ratePerCredit * 1000)) / 10) / 100;

/** What the pack costs the reseller. Rounded once, as the server does. */
export const packPrice = (pack) => priceForDays(pack.days, pack.ratePerCredit);

/** List value minus cost — computed from the rounded price so they add up. */
export const packMargin = (pack) => round2(pack.listPriceUsd - packPrice(pack));

/** How many one-year licenses the pack's credits cover. */
export const yearLicenses = (pack) => pack.days / DAYS_PER_YEAR;

/** What one 1-year license costs the reseller at this pack's rate. */
export const costPerYearLicense = (pack) => priceForDays(DAYS_PER_YEAR, pack.ratePerCredit);

/** What they keep reselling that license at list price. */
export const profitPerYearLicense = (pack) => round2(ONE_YEAR_LIST_PRICE - costPerYearLicense(pack));

/** Fraction of the list price kept, 0–1. */
export const marginPercent = (pack) => packMargin(pack) / pack.listPriceUsd;

export const allowsPayLater = (pack) => !!pack && PAY_LATER_PACK_IDS.includes(pack.id);

// ── Lookups ──────────────────────────────────────────────────────────────────

export const packById = (id) => CREDIT_PACKS.find((p) => p.id === id) || null;
export const nextPackUp = (pack) => {
  const i = CREDIT_PACKS.findIndex((p) => p.id === pack?.id);
  return i >= 0 && i < CREDIT_PACKS.length - 1 ? CREDIT_PACKS[i + 1] : null;
};
export const bestMarginPack = () => CREDIT_PACKS[CREDIT_PACKS.length - 1];
export const DEFAULT_PACK = CREDIT_PACKS.find((p) => p.badge === "most_chosen") || CREDIT_PACKS[0];

// ── Formatting ───────────────────────────────────────────────────────────────

/** "$657" / "$129.58" — drops .00 on round totals. */
export function money(amount) {
  const rounded = round2(amount);
  return `$${rounded.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "$0.355" — the per-credit rate needs its third place. */
export const rate = (n) => `$${n.toFixed(3)}`;

export const credits = (n) => Number(n || 0).toLocaleString("en-US");

export const formatPercent = (fraction) => `${Math.round(fraction * 100)}%`;
